import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type NewsSectionProps = {
  language: Language;
};
const news = {
  ua: [{
    id: 1,
    date: '15 січня 2024',
    title: 'CHAMALEON розширює присутність на ринку Східної Європи',
    excerpt: "Компанія підписала нові дистриб'юторські угоди з партнерами в Польщі та Чехії..."
  }, {
    id: 2,
    date: '8 січня 2024',
    title: 'Нова лінійка екологічних покриттів',
    excerpt: 'Представляємо інноваційну серію водорозчинних лаків з мінімальним вмістом VOC...'
  }, {
    id: 3,
    date: '28 грудня 2023',
    title: 'Підсумки року: рекордні показники продажів',
    excerpt: 'CHAMALEON завершує 2023 рік з найкращими результатами за останні 10 років...'
  }],
  en: [{
    id: 1,
    date: 'January 15, 2024',
    title: 'CHAMALEON Expands Presence in Eastern European Market',
    excerpt: 'The company has signed new distribution agreements with partners in Poland and Czech Republic...'
  }, {
    id: 2,
    date: 'January 8, 2024',
    title: 'New Line of Eco-Friendly Coatings',
    excerpt: 'Introducing an innovative series of water-based lacquers with minimal VOC content...'
  }, {
    id: 3,
    date: 'December 28, 2023',
    title: 'Year in Review: Record Sales Performance',
    excerpt: 'CHAMALEON concludes 2023 with the best results in the last 10 years...'
  }]
};
const sectionText = {
  ua: {
    title: 'Блог',
    viewAll: 'Усі статті',
    readMore: 'Читати'
  },
  en: {
    title: 'Blog',
    viewAll: 'All Posts',
    readMore: 'Read More'
  }
};
export function NewsSection({
  language
}: NewsSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const newsList = news[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-191">
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '100px 100px'
    }} data-id="element-192" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-193">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12" data-id="element-194">
          <div data-id="element-195">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-196">
              {t.title}
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-chamaleon-yellow to-transparent" data-id="element-197" />
          </div>
          <motion.a href="#" className="inline-flex items-center gap-2 text-chamaleon-yellow font-medium group text-sm sm:text-base pb-1" whileHover={{
          x: 5
        }} data-id="element-198">
            {t.viewAll}
            <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-199" />
          </motion.a>
        </motion.div>

        {/* News Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-200">
          {newsList.map((item, index) => <motion.a key={item.id} href="#" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} whileHover={{
          y: -8
        }} className="group block" data-id="element-201">
              <div className="glass-card rounded-2xl sm:rounded-3xl overflow-hidden h-full hover:border-chamaleon-yellow/40 hover:shadow-[0_10px_40px_rgba(255,184,28,0.15)] transition-all duration-500 flex flex-col group/card" data-id="element-202">
                {/* Image Visual */}
                <div className="relative h-56 sm:h-64 overflow-hidden bg-chamaleon-charcoal" data-id="element-203">
                  <img src="https://cdn.magicpatterns.com/uploads/wsupN2zZ5GnwiWGRutDqPf/Porsche-Panamera-linia-tyl-1.jpg" alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover/card:scale-110" data-id="element-204" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-90" data-id="element-205" />
                  <div className="absolute inset-0 bg-chamaleon-yellow/20 mix-blend-overlay opacity-0 group-hover/card:opacity-100 transition-opacity duration-500" data-id="element-206" />

                  {/* Date Badge floating on image */}
                  <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 flex items-center gap-2 text-white/90 text-xs sm:text-sm font-medium" data-id="element-207">
                    <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-chamaleon-yellow" data-id="element-208" />
                    {item.date}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between relative z-10 bg-gradient-to-b from-[#111111] to-transparent" data-id="element-209">
                  <div data-id="element-210">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover/card:text-chamaleon-yellow transition-colors line-clamp-2 leading-snug" data-id="element-211">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base mb-6 line-clamp-3 leading-relaxed" data-id="element-212">
                      {item.excerpt}
                    </p>
                  </div>

                  <span className="inline-flex items-center gap-2 text-chamaleon-yellow font-bold text-sm sm:text-base uppercase tracking-wider mt-auto" data-id="element-213">
                    {t.readMore}
                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover/card:translate-x-2 transition-transform duration-300" data-id="element-214" />
                  </span>
                </div>
              </div>
            </motion.a>)}
        </div>
      </div>
    </section>;
}